// ABOUTME: Handles trainer-athlete binding when athlete clicks invitation link.
// ABOUTME: Shows a generic confirmation modal and connects athlete to trainer.

import { useParams } from "react-router"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { CConfirmModal, CSpinner } from "@shared/ui"
import { useTranslation } from "react-i18next"
import { useMutation } from "@apollo/client/react"
import type { ErrorLike } from "@apollo/client"
import { SetTrainerDocument } from "@/graphql/main/gql/graphql"
import { useMeContext } from "@/model/user/provider"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { useErrorNotifier } from "@/app/providers/useErrorNotifier"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { SETTINGS_ROUTES } from "@shared/constants/navigation"

// We intentionally don't fetch the trainer's profile here: until the
// athlete-coach relationship exists, `user(userID)` denies the query with
// ErrAccessDenied, so the modal never opens. Show a generic confirmation.

const TrainerBindAthlete = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { me } = useMeContext()
  const { handleNotification } = useNotificationContext()
  const { notifyError } = useErrorNotifier()
  const { t } = useTranslation()
  const [isDismissed, setIsDismissed] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  // Set once the athlete accepts, and never cleared: from that moment this page is the one making
  // the connection, so "this coach is already connected" would be its own answer read back to it.
  const [isConnectingHere, setIsConnectingHere] = useState(false)

  const trainerID = params.id

  // Check if this trainer is already connected
  const isAlreadyConnected = me.userInfo.info.trainers?.some((trainer) => trainer?.common?.id === trainerID)

  // The confirmation is for a coach who is not connected yet, until the athlete cancels it.
  const isConfirm = Boolean(trainerID) && !isAlreadyConnected && !isDismissed

  const [setTrainer] = useMutation(SetTrainerDocument)

  const handleConnectTrainer = () => {
    if (!trainerID) return

    setIsProcessing(true)
    setIsConnectingHere(true)
    // `setTrainer` answers with the athlete's User, selected the way the `user` query reads it, so
    // the cache record updates itself and the coach list on the settings screen follows. The
    // deprecated `userSetTrainer` answered a Boolean, which is why this used to refetch the user.
    void setTrainer({
      variables: { trainerID },
      onCompleted: () => {
        handleNotification("success", t("common.coachConnect"), true, 3000)
        localStorage.removeItem(STORAGE_KEYS.BIND_TRAINER_LINK)
        setIsProcessing(false)
        navigate(SETTINGS_ROUTES.coach)
      },
      onError: (e: ErrorLike) => {
        notifyError(e)
        localStorage.removeItem(STORAGE_KEYS.BIND_TRAINER_LINK)
        setIsProcessing(false)
        navigate(SETTINGS_ROUTES.coach)
      },
    })
  }

  const handleCancel = () => {
    localStorage.removeItem(STORAGE_KEYS.BIND_TRAINER_LINK)
    setIsDismissed(true)
    navigate(SETTINGS_ROUTES.coach)
  }

  // An already connected coach needs no confirmation: say so, forget the link and go to the coach
  // settings. Not while this page is doing the connecting, though: `setTrainer` answers with the
  // User, so the record — and `me` with it — carries the new coach before the page has left, and
  // the athlete would be told twice, "connected" and then "already connected".
  useEffect(() => {
    if (!trainerID || !isAlreadyConnected || isConnectingHere) return
    handleNotification("success", t("common.trainerAlreadyConnected"), true, 3000)
    localStorage.removeItem(STORAGE_KEYS.BIND_TRAINER_LINK)
    navigate(SETTINGS_ROUTES.coach)
  }, [trainerID, isAlreadyConnected, isConnectingHere, handleNotification, t, navigate])

  return (
    <>
      {isProcessing && <CSpinner typeHeight="screen" />}
      <CConfirmModal
        isShow={isConfirm}
        onClose={handleCancel}
        onSuccess={handleConnectTrainer}
        isProcessing={isProcessing}
        description={`${t("common.wouldLikeConnectTrainer")}?`}
        confirmText={t("common.yes")}
      />
    </>
  )
}

export default TrainerBindAthlete
